import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import api from "../lib/api";
import { useCart } from "../context/CartContext";
import { Reveal } from "../components/Reveal";
import { Footer } from "../components/Footer";

function formatDescription(text) {
  if (!text) return null;
  const normalized = String(text)
    .replace(/\r/g, "")
    .replace(/\s*•\s*/g, "\n• ")
    .replace(/\s+-\s+/g, "\n- ");
  const blocks = normalized.split(/\n+/).map((s) => s.trim()).filter(Boolean);
  return blocks.map((b, i) => {
    if (b.startsWith("• ") || b.startsWith("- ")) {
      return (
        <p key={i} className="flex gap-3">
          <span className="text-wheat select-none">—</span>
          <span>{b.replace(/^[•-]\s*/, "")}</span>
        </p>
      );
    }
    return <p key={i}>{b}</p>;
  });
}

export default function PrintDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [size, setSize] = useState(null);
  const { addItem } = useCart();

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/prints/${id}`).then((r) => { setP(r.data); setSize(r.data.sizes[1]); });
  }, [id]);

  const addToCart = () => {
    if (!size) return;
    addItem({
      print_id: p.id, title: p.title, image: p.image,
      size_key: size.key, dimensions: size.dimensions, price: size.price,
    });
    toast.success(`Added "${p.title}" (${size.dimensions}) to your cart`);
  };

  if (!p) return <div className="pt-40 text-center label text-stone">Loading…</div>;

  return (
    <div className="pt-[7.25rem]">
      <Helmet>
        <title>{`${p.title} — ${p.category_name} Print | Prairie Prints MT`}</title>
        <meta name="description" content={`${(p.description || p.story || "").slice(0, 155)}`} />
        <link rel="canonical" href={`https://montana-landscapes.emergent.host/print/${p.id}`} />
        <meta property="og:type" content="product" />
        <meta property="og:title" content={`${p.title} | Prairie Prints MT`} />
        <meta property="og:image" content={p.image} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content={p.image} />
      </Helmet>
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-12 md:py-20 grid lg:grid-cols-2 gap-12 lg:gap-20">
        <div className="lg:sticky lg:top-32 self-start">
          <div className="overflow-hidden border border-line bg-paper-2">
            <img src={p.image} alt={p.title} className="w-full h-auto object-cover max-h-[80vh]" data-testid="print-hero-image" />
          </div>
        </div>

        <div>
          <Link to={`/gallery/${p.category}`} className="label text-brick hover:underline">← {p.category_name}</Link>
          <h1 className="font-serif text-5xl md:text-6xl tracking-tight mt-4" data-testid="print-title">{p.title}</h1>
          <div className="text-stone mt-6 text-lg leading-relaxed space-y-4">{formatDescription(p.description || p.story)}</div>

          <div className="mt-10">
            <span className="label text-ink">Choose a size</span>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
              {p.sizes.map((s) => (
                <button key={s.key} data-testid={`size-${s.key}`} onClick={() => setSize(s)}
                  className={`text-left border p-4 transition-colors duration-300 ${size?.key === s.key ? "border-ink bg-ink text-paper" : "border-line hover:border-ink"}`}>
                  <div className="font-serif text-2xl">{s.dimensions}</div>
                  <div className="mt-2 font-sans">${s.price.toFixed(2)}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="mt-8 flex items-center gap-6 flex-wrap">
            <span className="font-serif text-4xl" data-testid="print-price">${size?.price.toFixed(2)}</span>
            <button data-testid="add-to-cart-btn" onClick={addToCart}
              className="label bg-ink text-paper px-10 py-4 hover:bg-brick transition-colors duration-300">
              Add to cart
            </button>
          </div>
          <p className="label text-stone mt-3">Buy 2 prints save 15% · buy 3 save 25%</p>

          <ul className="mt-10 border-t border-line pt-6 space-y-2 text-stone">
            <li className="flex justify-between"><span className="label">Paper</span><span>Premium archival · 100-yr rated</span></li>
            <li className="flex justify-between"><span className="label">Printing</span><span>Professional photo lab, USA</span></li>
            <li className="flex justify-between"><span className="label">Shipping</span><span>Fast &amp; free · unframed</span></li>
          </ul>
        </div>
      </section>

      {p.related?.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-6 md:px-10 pb-24">
          <h2 className="font-serif text-4xl tracking-tight mb-10">More from {p.category_name}</h2>
          <div className="grid sm:grid-cols-3 gap-8">
            {p.related.map((r, i) => (
              <Reveal key={r.id} delay={i * 0.08}>
                <Link to={`/print/${r.id}`} data-testid={`related-${r.id}`} className="group block">
                  <div className="overflow-hidden border border-line aspect-[4/3]">
                    <img src={r.image} alt={r.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1200ms]" />
                  </div>
                  <h3 className="font-serif text-xl mt-3">{r.title}</h3>
                </Link>
              </Reveal>
            ))}
          </div>
        </section>
      )}
      <Footer />
    </div>
  );
}
